// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Clatter — the three modes. A mode must change how the game PLAYS, not just a number. The axes
// here are the DICE BUDGET, the SCORING SHAPE, and — for Updraft — the direction of the spill,
// which is the game's signature verb: inverting it inverts every heuristic a player has built.
//
// `turns` is the number of ACTIVE turns in the whole round, and it is deliberately divisible by 2,
// 3 AND 4 so that every supported player count gets a whole number of active turns and an
// IDENTICAL dice budget (`turns` opportunities each: `turns/n` active + `turns − turns/n` passive).
// That is the game's fairness-by-construction, and tests/balance.test.ts holds it.
//
// The HOST's pick travels frozen inside rematch.ts's round start; everything else is DERIVED from
// mode + seed on every peer, so a mode can never desync. modeOf() guards an id off the wire with
// Object.hasOwn (a bare `MODES[id] ||` lets 'constructor' through as a Mode of undefined fields).

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** How many dice are rolled for an active turn. */
  pool: number;
  /** ACTIVE turns in the whole round — see the note above on divisibility. */
  turns: number;
  /**
   * Scramble: only this many of your four tracks score — your best ones. 0 means all four count.
   *
   * It replaced "a track you FINISH scores double", which the balance sim condemned: in a 12-turn
   * mode nobody finishes a track unless the dice hand it to them, so the doubling was a coin flip
   * worth twenty points. The leader after four of twelve turns won 70% of games. Counting only your
   * best two tracks asks the same question — commit, do not spread — with none of the swing, because
   * dropping your worst track is variance REDUCTION.
   */
  scoreBest: number;
  /**
   * Tribute: every rival who takes a die out of YOUR spill pays you a point. It inverts the sign of
   * the game's central term — in Workshop you are minimising what the table gets, in Tribute you are
   * pricing it — without touching the sheet, the UI or the netcode.
   */
  tribute: boolean;
  /** Seconds on the clock for one decision in a networked round. */
  clockMs: number;
}

export const MODES: Record<string, Mode> = {
  workshop: {
    id: 'workshop',
    name: 'Workshop',
    blurb: 'Six dice, twenty-four turns. Room to spread across all four tracks — and every high die you want costs the table a handful.',
    pool: 6,
    turns: 24,
    scoreBest: 0,
    tribute: false,
    clockMs: 22_000,
  },
  scramble: {
    id: 'scramble',
    name: 'Scramble',
    blurb: 'Five dice, half the turns — and only your best TWO tracks count. There is no time to spread: pick your two and pour everything into them.',
    pool: 5,
    turns: 12,
    scoreBest: 2,
    tribute: false,
    clockMs: 20_000,
  },
  tribute: {
    id: 'tribute',
    name: 'Tribute',
    blurb: 'Every rival who takes a die out of your spill pays you a point. Suddenly the question is not how little you can give away — it is how much it is worth selling.',
    pool: 6,
    turns: 24,
    scoreBest: 0,
    tribute: true,
    clockMs: 22_000,
  },
};

export const MODE_IDS: string[] = ['workshop', 'scramble', 'tribute'];
export const DEFAULT_MODE = 'workshop';

export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}

/** Every supported player count divides every mode's turn budget. Pinned by a test. */
export const PLAYER_COUNTS = [2, 3, 4] as const;
export const MAX_PLAYERS = 4;
